import { ClipboardList, FileText, Package, Search, ShoppingCart, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  RECHERCHE_MIN,
  TYPE_RESULTAT_LABELS,
  grouperResultats,
  lienResultat,
  type ResultatRecherche,
  type TypeResultat,
} from '@sirajoptic/shared';
import { Spinner } from '@/components/ui/feedback';
import { useCoucheModale } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const ICONES: Record<TypeResultat, ComponentType<{ className?: string }>> = {
  CLIENT: Users,
  COMMANDE: ClipboardList,
  VENTE: ShoppingCart,
  FACTURE: FileText,
  PRODUIT: Package,
};

/**
 * Palette de recherche — une boîte pour tout l'atelier.
 *
 * On ne part presque jamais d'un écran : on part d'un nom au téléphone, d'un
 * numéro de bon sur un sachet, d'une référence sur une facture. Chercher
 * supposait jusqu'ici de savoir d'avance dans quelle liste regarder.
 *
 * Le clavier fait tout : Ctrl+K ouvre, les flèches parcourent, Entrée ouvre,
 * Échap referme. C'est le point : au comptoir, une main tient le combiné.
 */
export function PaletteRecherche({
  ouvert,
  onFermer,
  resultats,
  chargement,
  terme,
  onChangeTerme,
}: {
  ouvert: boolean;
  onFermer: () => void;
  resultats: ResultatRecherche[];
  chargement: boolean;
  terme: string;
  onChangeTerme: (v: string) => void;
}) {
  const [actif, setActif] = useState(0);
  const naviguer = useNavigate();
  const conteneur = useCoucheModale(ouvert, onFermer);
  const listeRef = useRef<HTMLUListElement>(null);

  const groupes = useMemo(() => grouperResultats(resultats), [resultats]);
  /* La liste aplatie porte l'ordre du clavier : les flèches traversent les
   * groupes sans que l'utilisateur ait à savoir qu'il y en a. */
  const plat = useMemo(() => groupes.flatMap((g) => g.resultats), [groupes]);

  // Un nouveau terme repart du premier résultat, jamais d'un index périmé.
  useEffect(() => setActif(0), [terme]);

  // Garde la ligne active visible quand on descend au clavier.
  useEffect(() => {
    if (!ouvert) return;
    listeRef.current
      ?.querySelector<HTMLElement>('[data-actif="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [actif, ouvert]);

  if (!ouvert) return null;

  function ouvrir(r: ResultatRecherche) {
    onFermer();
    naviguer(lienResultat(r));
  }

  function surTouche(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActif((i) => (plat.length === 0 ? 0 : (i + 1) % plat.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((i) => (plat.length === 0 ? 0 : (i - 1 + plat.length) % plat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cible = plat[actif];
      if (cible) ouvrir(cible);
    }
  }

  const assezLong = terme.trim().length >= RECHERCHE_MIN;
  let index = -1;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]">
      <div className="fixed inset-0 animate-fade-in bg-ink/40" onClick={onFermer} aria-hidden />

      <div
        ref={conteneur}
        role="dialog"
        aria-modal="true"
        aria-label="Recherche globale"
        className="relative flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-line bg-white shadow-xl"
      >
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search aria-hidden className="h-4 w-4 shrink-0 text-ink-muted" />
          <input
            value={terme}
            onChange={(e) => onChangeTerme(e.target.value)}
            onKeyDown={surTouche}
            placeholder="Client, n° de bon, facture, produit…"
            aria-label="Rechercher partout"
            className="min-w-0 flex-1 border-0 bg-transparent py-3 text-sm outline-none placeholder:text-ink-muted"
          />
          {chargement ? <Spinner className="h-4 w-4 shrink-0 text-ink-muted" /> : null}
        </div>

        <ul ref={listeRef} className="min-h-0 flex-1 overflow-y-auto py-1">
          {!assezLong ? (
            <li className="px-4 py-8 text-center text-sm text-ink-muted">
              Tapez au moins deux caractères.
            </li>
          ) : plat.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-ink-muted">
              {chargement ? 'Recherche…' : `Rien ne correspond à « ${terme.trim()} ».`}
            </li>
          ) : (
            groupes.map((g) => (
              <li key={g.type}>
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  {TYPE_RESULTAT_LABELS[g.type]}
                </p>
                <ul>
                  {g.resultats.map((r) => {
                    index += 1;
                    const i = index;
                    const Icone = ICONES[r.type];
                    return (
                      <li key={`${r.type}-${r.id}`}>
                        <button
                          type="button"
                          data-actif={i === actif}
                          onMouseMove={() => setActif(i)}
                          onClick={() => ouvrir(r)}
                          className={cn(
                            'flex w-full items-center gap-3 px-3 py-2 text-left text-sm',
                            i === actif ? 'bg-mustard-50' : 'hover:bg-surface-sunken',
                          )}
                        >
                          <Icone
                            aria-hidden
                            className={cn(
                              'h-4 w-4 shrink-0',
                              i === actif ? 'text-mustard-600' : 'text-ink-muted',
                            )}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-ink">{r.libelle}</span>
                            {r.description ? (
                              <span className="block truncate text-xs text-ink-muted">
                                {r.description}
                              </span>
                            ) : null}
                          </span>
                          {r.complement ? (
                            <span className="shrink-0 tabular text-xs text-ink-muted">
                              {r.complement}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))
          )}
        </ul>

        <div className="flex items-center gap-3 border-t border-line bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
          <span>
            <kbd className="rounded border border-line bg-white px-1">↑</kbd>{' '}
            <kbd className="rounded border border-line bg-white px-1">↓</kbd> parcourir
          </span>
          <span>
            <kbd className="rounded border border-line bg-white px-1">↵</kbd> ouvrir
          </span>
          <span>
            <kbd className="rounded border border-line bg-white px-1">Échap</kbd> fermer
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
